import { useEffect, useRef } from 'react';

const APP_NAME = 'SquareUp';

/**
 * Sets the document title with optional app name suffix.
 * Restores the previous title on unmount.
 *
 * @param title - The page title (e.g., "Profile Settings")
 * @param options - Configuration options
 * @param options.suffix - Whether to append app name (default: true)
 * @param options.restoreOnUnmount - Whether to restore previous title on unmount (default: true)
 *
 * @example
 * // Sets title to "Profile Settings | SquareUp"
 * useDocumentTitle('Profile Settings');
 *
 * @example
 * // Sets title to "Super Bowl Board" (no suffix)
 * useDocumentTitle('Super Bowl Board', { suffix: false });
 */
export function useDocumentTitle(
  title: string | undefined,
  options: { suffix?: boolean; restoreOnUnmount?: boolean } = {}
): void {
  const { suffix = true, restoreOnUnmount = true } = options;
  const previousTitle = useRef<string>(document.title);

  useEffect(() => {
    if (!title) return;

    const newTitle = suffix ? `${title} | ${APP_NAME}` : title;
    // Capture the current previous title for cleanup
    const savedPreviousTitle = previousTitle.current;
    document.title = newTitle;

    return () => {
      if (restoreOnUnmount) {
        document.title = savedPreviousTitle;
      }
    };
  }, [title, suffix, restoreOnUnmount]);
}
